import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/support"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Support Hub
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground">Last updated: December 2024</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-white/10 rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none space-y-8">
                
                {/* Introduction */}
                <div>
                  <p className="text-muted-foreground leading-relaxed">
                    This privacy notice for Playmatez Pty Ltd, describes how and why we might collect, store, use, and/or share your information when you use our services, such as when you:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                    <li>Visit or use our website at https://www.playmatez.io, or any website of ours that links to this privacy notice.</li>
                    <li>Download and use our mobile application (Playmatez), or any other application of ours that links to this privacy notice.</li>
                    <li>Engage with us in other related ways, including any sales, marketing, or events.</li>
                  </ul>
                  <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-xl">
                    <p className="text-white font-medium">NOTE:</p>
                    <p className="text-muted-foreground text-sm mt-1">
                      If you do not agree with our policies and practices, we would kindly request you to refrain from using our Services. If you still have any questions or concerns, please contact us at support@playmatez.io
                    </p>
                  </div>
                </div>

                {/* Summary of Key Points */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">Summary of Key Points</h2>
                  <p className="text-muted-foreground mb-4">
                    This summary provides key points from our privacy notice, but you can find out more details about any of these topics in the sections that follow below and in the support hub of our website.
                  </p>
                  <div className="space-y-4">
                    {[
                      { q: "1. What personal information do we process?", a: "When you visit, use, or navigate our Services, we may process personal information depending on how you interact with Playmatez and the Services, the choices you make, and the products and features you use." },
                      { q: "2. Do we process any sensitive personal information?", a: "We may process sensitive personal information when necessary, with your consent or as otherwise permitted by applicable law." },
                      { q: "3. Do we receive any information from third parties?", a: "We may receive information from public databases, marketing partners, social media platforms, and other outside sources." },
                      { q: "4. How do we process your information?", a: "We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so." },
                      { q: "5. In what situations and with which types of parties do we share personal information?", a: "We may share information in specific situations and with specific categories of third parties." },
                      { q: "6. How do we keep your information safe?", a: "We have organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information." },
                      { q: "7. What are your rights?", a: "Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information." },
                      { q: "8. How do you exercise your rights?", a: "The easiest way to exercise your rights is by contacting us from the contact us section on our web page https://www.playmatez.io. We will consider and act upon any request in accordance with applicable data protection laws." },
                    ].map((item, i) => (
                      <div key={i} className="p-4 bg-white/5 rounded-xl">
                        <p className="text-white font-medium">{item.q}</p>
                        <p className="text-muted-foreground text-sm mt-1">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table of Contents */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">Table of Contents</h2>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>What Information Do We Collect?</li>
                    <li>How Do We Process Your Information?</li>
                    <li>What Legal Bases Do We Rely On To Process Your Personal Information?</li>
                    <li>When And With Whom Do We Share Your Personal Information?</li>
                    <li>What Is Our Stance On Third-Party Websites?</li>
                    <li>Do We Use Cookies And Other Tracking Technologies?</li>
                    <li>How Do We Handle Your Social Logins?</li>
                    <li>Is Your Information Transferred Internationally?</li>
                    <li>How Long Do We Keep Your Information?</li>
                    <li>How Do We Keep Your Information Safe?</li>
                    <li>What Are Your Privacy Rights?</li>
                    <li>Controls For Do-Not-Track Features</li>
                    <li>Do We Make Updates To This Notice?</li>
                    <li>How Can You Contact Us About This Notice?</li>
                    <li>How Can You Review, Update, Or Delete The Data We Collect From You?</li>
                  </ol>
                </div>

                {/* Section 1 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">1. What Information Do We Collect?</h2>
                  <p className="text-muted-foreground mb-4">
                    <strong className="text-white">We collect personal information that you provide to us.</strong>
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
                  </p>
                  <p className="text-muted-foreground mb-4">
                    The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground mb-4">
                    <li>Names</li>
                    <li>Email addresses</li>
                    <li>Phone numbers</li>
                    <li>Job titles</li>
                    <li>Usernames</li>
                    <li>Passwords</li>
                    <li>Billing addresses</li>
                    <li>Debit/Credit card numbers</li>
                    <li>Contact or Authentication data</li>
                    <li>Sensitive Information</li>
                  </ul>
                  
                  <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Sensitive Information</h3>
                  <p className="text-muted-foreground mb-4">
                    When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information:
                  </p>
                  <p className="text-muted-foreground mb-2"><strong className="text-white">i. Social security numbers or other government identifiers</strong></p>
                  <p className="text-muted-foreground mb-4"><strong className="text-white">ii. Payment Data.</strong> We may collect data necessary to process your payment if you make purchases, such as your payment instrument number, and the security code associated with your payment instrument. All payment data is stored by Stripe/Razorpay.</p>
                  <p className="text-muted-foreground mb-4"><strong className="text-white">iii. Social Media Login Data.</strong> We may provide you with the option to register with us using your existing social media account details, like your Google account.</p>
                  <p className="text-muted-foreground mb-4"><strong className="text-white">iv. Application Data.</strong> If you use our application(s), we also may collect the following information if you choose to provide us with access or permission:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                    <li><strong className="text-white">Geolocation Information</strong> - We may request access or permission to track location-based information from your mobile device, continuously or while using our mobile application(s), to provide certain location-based services.</li>
                    <li><strong className="text-white">Mobile Device Access</strong> - We may request access or permission to certain features from your mobile device, including your mobile device's camera, calendar, microphone, and other features.</li>
                    <li><strong className="text-white">Mobile Device Data</strong> - We automatically collect device information (such as your mobile device ID, model, and manufacturer), operating system, version information and system configuration information.</li>
                    <li><strong className="text-white">Push Notifications</strong> - We may request to send you push notifications regarding your account or certain features of the application(s).</li>
                  </ul>

                  <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Information Automatically Collected</h3>
                  <p className="text-muted-foreground mb-4">
                    <em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em>
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information.
                  </p>
                  <p className="text-muted-foreground mb-4">The information we collect includes:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-white">Log and Usage Data</strong> - Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services.</li>
                    <li><strong className="text-white">Location Data</strong> - We collect location data such as information about your device's location, which can be either precise or imprecise.</li>
                  </ul>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">2. How Do We Process Your Information?</h2>
                  <p className="text-muted-foreground mb-4">
                    <em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</em>
                  </p>
                  <p className="text-muted-foreground mb-4">We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-white">To facilitate account creation and authentication</strong> and otherwise manage user accounts.</li>
                    <li><strong className="text-white">To deliver and facilitate delivery of services</strong> to the user.</li>
                    <li><strong className="text-white">To respond to user inquiries/offer support</strong> to users.</li>
                    <li><strong className="text-white">To fulfill and manage your orders</strong> and payments made through the Services.</li>
                    <li><strong className="text-white">To enable user-to-user communications</strong> if you choose to use any of our offerings that allow for communication with another user.</li>
                    <li><strong className="text-white">To request feedback</strong> and to contact you about your use of our Services.</li>
                    <li><strong className="text-white">To send you marketing and promotional communications</strong> if this is in accordance with your marketing preferences.</li>
                    <li><strong className="text-white">To deliver targeted advertising</strong> tailored to your interests, location, and more.</li>
                    <li><strong className="text-white">To protect our Services</strong> including fraud monitoring and prevention.</li>
                    <li><strong className="text-white">To identify usage trends</strong> to better understand how they are being used so we can improve them.</li>
                    <li><strong className="text-white">To determine the effectiveness</strong> of our marketing and promotional campaigns.</li>
                    <li><strong className="text-white">To save or protect an individual's vital interest</strong> such as to prevent harm.</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">3. What Legal Bases Do We Rely On To Process Your Information?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e. legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.</em>
                  </p>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">4. When And With Whom Do We Share Your Personal Information?</h2>
                  <p className="text-muted-foreground mb-4">
                    <em>In short: We may share your information with trusted third parties in limited circumstances to help run our services smoothly and securely.</em>
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We may work with third-party vendors, consultants, contractors, or service providers ("third parties") who assist us in operating our app, processing payments, analyzing data, delivering notifications, or providing other core services.
                  </p>
                  <p className="text-muted-foreground mb-4">These third parties may include:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground mb-4">
                    <li>Ad Networks</li>
                    <li>Affiliate Marketing Platforms</li>
                    <li>Data Analytics Tools</li>
                    <li>Payment Processors</li>
                    <li>Order Fulfilment Partners</li>
                    <li>Third-Party Plugins or APIs</li>
                  </ul>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-white">Business Transfers</strong> - We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.</li>
                    <li><strong className="text-white">Google Maps Platform APIs</strong> - We may share your information with certain Google Maps Platform APIs.</li>
                    <li><strong className="text-white">Affiliates</strong> - We may share your information with our affiliates.</li>
                    <li><strong className="text-white">Business Partners</strong> - We may share your information with our business partners to offer you certain products, services, or promotions.</li>
                    <li><strong className="text-white">Other Users</strong> - When you share personal information or interact with public areas of the Services, such personal information may be viewed by all users.</li>
                    <li><strong className="text-white">Offer Wall</strong> - Our application(s) may display a third-party hosted 'offer wall'.</li>
                  </ul>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">5. What Is Our Stance On Third-Party Websites?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We are not responsible for the safety of any information that you share with third parties that we may link to or who advertise on our Services, but are not affiliated with, our Services.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    The Services may link to third-party websites, online services, or mobile applications and/or contain advertisements from third parties that are not affiliated with us. We cannot guarantee the safety and privacy of data you provide to any third parties. Any data collected by third parties is not covered by this privacy notice.
                  </p>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">6. Do We Use Cookies And Other Tracking Technologies?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information.
                  </p>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">7. How Do We Handle Your Social Logins?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    Our Services offer you the ability to register and log in using your third-party social media account details (like your Google logins and Apple logins). Where you choose to do this, we will receive certain profile information about you from your social media provider.
                  </p>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">8. Is Your Information Transferred Internationally?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We may transfer, store, and process your information in countries other than your own.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    Our servers are located in Australia. If you are accessing our Services from outside Australia, please be aware that your information may be transferred to, stored, and processed by us in our facilities and by those third parties with whom we may share your personal information.
                  </p>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">9. How Long Do We Keep Your Information?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We keep your information for as long as necessary to fulfil the purposes outlined in this privacy notice unless otherwise required by law.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, unless a longer retention period is required or permitted by law. When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymise such information.
                  </p>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">10. How Do We Keep Your Information Safe?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: We aim to protect your personal information through a system of organisational and technical security measures.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    We have implemented appropriate and reasonable technical and organisational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
                  </p>
                </div>

                {/* Section 11 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">11. What Are Your Privacy Rights?</h2>
                  <div className="space-y-4">
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Access and Update</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to access, update, or delete your personal information at any time by logging into your account or contacting us directly.</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Rectification</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to have your information rectified if that information is inaccurate or incomplete.</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Erasure</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to request that we delete or remove your personal information under certain conditions.</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Restrict Processing</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to request that we restrict the processing of your personal information under certain conditions.</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Data Portability</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to request a copy of your personal information in a structured, machine-readable format.</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-white font-medium">Object to Processing</p>
                      <p className="text-muted-foreground text-sm mt-1">You have the right to object to our processing of your personal information under certain conditions.</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground mt-4">
                    If you have questions or comments about your privacy rights, you may email us at <a href="mailto:support@playmatez.io" className="text-primary hover:underline">support@playmatez.io</a>
                  </p>
                </div>

                {/* Section 12 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">12. Controls For Do-Not-Track Features</h2>
                  <p className="text-muted-foreground">
                    Most web browsers and some mobile operating systems include a Do-Not-Track ('DNT') feature or setting you can activate to signal your privacy preference. At this stage no uniform technology standard for recognising and implementing DNT signals has been finalised. As such, we do not currently respond to DNT browser signals.
                  </p>
                </div>

                {/* Section 13 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">13. Do We Make Updates To This Notice?</h2>
                  <p className="text-muted-foreground">
                    <em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em>
                  </p>
                  <p className="text-muted-foreground mt-4">
                    We may update this privacy notice from time to time. The updated version will be indicated by an updated 'Revised' date and the updated version will be effective as soon as it is accessible.
                  </p>
                </div>

                {/* Section 14 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">14. How Can You Contact Us About This Notice?</h2>
                  <p className="text-muted-foreground">
                    If you have questions or comments about this notice, you may email us at:
                  </p>
                  <p className="text-primary mt-2">support@playmatez.io</p>
                  <p className="text-muted-foreground mt-4">or by post to:</p>
                  <p className="text-white mt-2">
                    Playmatez<br />
                    526/368, Surrey Hills, Sydney 2000 NSW.
                  </p>
                </div>

                {/* Section 15 */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">15. How Can You Review, Update, Or Delete The Data We Collect From You?</h2>
                  <p className="text-muted-foreground">
                    Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request to review, update, or delete your personal information, please submit a request to <a href="mailto:support@playmatez.io" className="text-primary hover:underline">support@playmatez.io</a>
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
